import { Close } from '@mui/icons-material';
import { LoadingButton, TabContext, TabList, TabPanel } from '@mui/lab';
import { Backdrop, Box, Button, CircularProgress, Container, Tab } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useActiveClassroom, useActiveSimulation } from '../../active-simulation/useActiveSimulation';
import { useStartSimulation } from '../../active-simulation/useStartSimulation';
import { useInstructorExercise, useStartClassroomSession } from '../../api';
import AppBar from '../../app-bar/AppBar';
import ksimConsole2 from '../../assets/ksim-console2.png';
import AssignmentDetails from '../../assignment-details/AssignmentDetails';
import { useCheckRole } from '../../auth';
import BackLink from '../../back-link/BackLink';
import Breadcrumbs from '../../breadcrumbs/Breadcrumbs';
import Briefing from '../../briefing/Briefing';
import EndExerciseDialog from '../../common/dialogs/EndExerciseDialog';
import EditAssignmentDetails from '../../edit-assignment-details/EditAssignmentDetails';
import EditTitleDialog from '../../edit-title-dialog/EditTitleDialog';
import ExerciseHeader from '../../exercise-header/ExerciseHeader';
import ExerciseStudents from '../../exercise-students/ExerciseStudents';
import Fallback from '../../fallback/Fallback';
import KSimHeader from '../../k-sim-header/KSimHeader';
import { apiPaths, queryClient } from '../../queryClient';
import SimulationErrorDialog from '../../simulation-error-dialog/SimulationErrorDialog';
import SimulatorCard from '../../simulator-card/SimulatorCard';
import ExerciseSkeleton from '../../skeletons/ExerciseSkeleton';
import {
  ApiInstructorExercise,
  ExerciseState,
  ExerciseType,
  InstructorExercise,
  LoadingSimulation,
  SimulationRole,
} from '../../types';
import { Pages } from '../../types/pages';
import { useDesktop } from '../../useDesktop';
import { isInstructorClassroom, isRunning } from '../../utils/activeSimulation';
import { parseTime } from '../../utils/parseTime';
import { useActiveCompany } from '../../utils/useCompany';

const ExerciseDetail = () => {
  useCheckRole(['Instructor']);

  const activeCompany = useActiveCompany();
  const [selectedTab, selectTab] = useState('students');
  const onTabChange = (event: React.SyntheticEvent, newValue: string) => selectTab(newValue);

  const [showEditTitleDialog, setShowEditTitleDialog] = useState(false);

  const { exerciseId } = useParams();
  const isDesktop = useDesktop();

  const { activeClassroom } = useActiveClassroom();
  const isHostingThisClassroom = isInstructorClassroom(activeClassroom) && exerciseId === activeClassroom.exercise.id;
  const { activeSimulation, setActiveSimulation } = useActiveSimulation();

  const { exercise, isLoadingExercise, exerciseError } = useInstructorExercise(exerciseId);

  const { simulationStarting, startSimulation, startSimulationError } = useStartSimulation({
    exerciseId: null,
    assignmentGroupId: exerciseId,
    onSuccess: data => {
      const loadingSimulation: LoadingSimulation = {
        exercise: { id, name: exerciseFile.name, type },
        id: data.sessionId,
        message: `Launching ${exerciseFile.name ?? 'exercise'}`,
        progress: 0,
        role: SimulationRole.Instructor,
        simulator: {
          type: simulator.type,
        },
      };

      setActiveSimulation(loadingSimulation);
    },
    onError() {
      setActiveSimulation(null);
    },
  });

  const navigate = useNavigate();
  const [showLoadingBackdrop, setShowLoadingBackdrop] = useState(false);
  const [open, setShowConfirmDialog] = useState(false);

  const { startClassroomSession, sessionStarting, startClassroomSessionError } = useStartClassroomSession(
    exerciseId,
    () => setShowLoadingBackdrop(true),
    () => setShowLoadingBackdrop(false),
  );

  const onCompleted = async () => {
    setShowConfirmDialog(false);

    await queryClient.fetchQuery({
      queryKey: [{ path: apiPaths.teach.exercise(activeCompany?.companyId, exerciseId) }],
    });
  };

  const onDeleted = () => {
    setShowConfirmDialog(false);
    setShowLoadingBackdrop(true);

    setTimeout(() => navigate(Pages.Teach), 3500);
  };

  const onError = () => {
    setShowConfirmDialog(false);
  };

  // Show loading dialog until we get notified of an active instructor classroom session
  useEffect(() => {
    if (showLoadingBackdrop && isInstructorClassroom(activeClassroom)) {
      navigate(Pages.Classroom);
      setShowLoadingBackdrop(false);
    }
  }, [showLoadingBackdrop, activeClassroom]);

  if (isLoadingExercise) {
    return <ExerciseSkeleton />;
  }

  if (exerciseError || !exercise) {
    throw exerciseError;
  }

  const mappedExercise: InstructorExercise = parseTime<ApiInstructorExercise>(exercise);
  const { brief, exercise: exerciseFile, id, scenario, simulator, title, type } = mappedExercise;

  const start = () => startSimulation();
  const finishedExercise = exerciseFile.state === ExerciseState.Finished;

  return (
    <>
      <AppBar title="Exercise" />
      <Breadcrumbs
        items={[{ label: 'Home', to: '/teach' }, { label: exerciseFile.name }]}
        sx={{ display: { xs: 'none', sm: 'flex' } }}
      />
      <Container>
        <BackLink label="Back to Home" sx={{ display: { xs: 'inline-block', sm: 'none' }, my: 2 }} to="/" />
        <KSimHeader
          adHoc={exerciseFile.adHoc}
          exerciseType={type}
          simulatorType={simulator.type}
          sx={{ mt: 1, mb: { xs: 2, sm: 4 } }}
        />

        <ExerciseHeader
          name={exerciseFile.name}
          simulatorType={simulator.type}
          title={title}
          type={type}
          onClickEdit={!finishedExercise ? () => setShowEditTitleDialog(true) : undefined}
          startButton={
            !finishedExercise &&
            (exercise.type === ExerciseType.SelfStudy ? (
              <>
                <LoadingButton onClick={() => setShowConfirmDialog(true)} variant="outlined" startIcon={<Close />}>
                  End exercise
                </LoadingButton>
                <LoadingButton
                  disabled={isRunning(activeSimulation)}
                  loading={simulationStarting}
                  onClick={start}
                  variant="contained"
                >
                  Start simulation
                </LoadingButton>
              </>
            ) : isHostingThisClassroom ? (
              <Button component={RouterLink} to={Pages.Classroom} variant="contained">
                Return to team session
              </Button>
            ) : (
              <>
                <LoadingButton onClick={() => setShowConfirmDialog(true)} variant="outlined" startIcon={<Close />}>
                  End exercise
                </LoadingButton>
                <LoadingButton
                  disabled={isRunning(activeSimulation) || !!activeClassroom?.id}
                  loading={sessionStarting}
                  onClick={() => startClassroomSession()}
                  variant="contained"
                >
                  Set up new simulation
                </LoadingButton>
              </>
            ))
          }
        />

        <Box sx={{ display: { sm: 'grid' }, gridTemplateColumns: '6fr minmax(0, 4fr)', gap: 11, mt: 3 }}>
          <Box pt={{ sm: 6 }} mb={3} gridColumn={2}>
            {finishedExercise ? (
              <AssignmentDetails {...mappedExercise} showAssignmentVisibility sx={{ mb: 2 }} />
            ) : (
              <EditAssignmentDetails {...mappedExercise} sx={{ mb: 2 }} />
            )}

            <SimulatorCard
              adHoc={exerciseFile.adHoc}
              exerciseType={type}
              name={simulator.name}
              simulatorType={simulator.type}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            />
          </Box>

          <Box gridRow={1}>
            <TabContext value={selectedTab}>
              <TabList
                onChange={onTabChange}
                sx={{ borderBottom: 1, borderColor: 'divider' }}
                variant={isDesktop ? 'standard' : 'fullWidth'}
              >
                <Tab label="Students" value="students" />
                <Tab label="Scenario" value="scenario" />
                <Tab label="Student briefing" value="briefing" />
              </TabList>

              <TabPanel value="students">
                <ExerciseStudents
                  simulatorId={simulator.id}
                  exerciseId={exerciseId!}
                  exerciseState={exerciseFile.state}
                />
              </TabPanel>
              <TabPanel value="scenario">
                {scenario.length ? (
                  <Briefing sections={scenario} />
                ) : (
                  <Fallback imgSrc={ksimConsole2} message="There is no scenario for this exercise" />
                )}
              </TabPanel>
              <TabPanel value="briefing">
                {brief.length ? (
                  <Briefing sections={brief} />
                ) : (
                  <Fallback imgSrc={ksimConsole2} message="There is no briefing for this exercise" />
                )}
              </TabPanel>
            </TabContext>
          </Box>
        </Box>
      </Container>

      <EditTitleDialog id={id} open={showEditTitleDialog} onClose={() => setShowEditTitleDialog(false)} title={title} />

      <SimulationErrorDialog error={startClassroomSessionError ?? undefined} retry={startClassroomSession} />
      <SimulationErrorDialog error={startSimulationError ?? undefined} retry={start} />

      <Backdrop
        sx={theme => ({ color: theme.palette.primary.contrastText, zIndex: theme.zIndex.drawer + 1 })}
        open={showLoadingBackdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <EndExerciseDialog
        open={open}
        exerciseId={exercise.id}
        onDeleted={onDeleted}
        onCompleted={onCompleted}
        onError={onError}
        onClose={() => setShowConfirmDialog(false)}
      />
    </>
  );
};

export default ExerciseDetail;
